import React, { useEffect, useRef, useState, useCallback } from "react";

const W = 480, H = 200, GROUND_Y = H - 40;
const CHEF_X = 60, CHEF_W = 36, CHEF_H = 48;
const GRAVITY = 0.6, JUMP_FORCE = -13, INITIAL_SPEED = 4, SPEED_INCREMENT = 0.00008;

const OBSTACLES = [
  { emoji: "🍕", w: 36, h: 36 }, { emoji: "🍝", w: 36, h: 36 }, { emoji: "🥘", w: 36, h: 36 },
  { emoji: "🍖", w: 36, h: 36 }, { emoji: "🥗", w: 40, h: 36 }, { emoji: "🍜", w: 36, h: 36 },
  { emoji: "🥩", w: 40, h: 32 }, { emoji: "🍾", w: 24, h: 52 }, { emoji: "🧂", w: 24, h: 48 },
];

const C = {
  bg: "#0a0a0f", surface: "rgba(255,255,255,0.04)", surfaceHover: "rgba(255,255,255,0.07)",
  border: "rgba(255,255,255,0.08)", text: "#f5f5f7",
  secondary: "rgba(245,245,247,0.55)", tertiary: "rgba(245,245,247,0.30)",
  orange: "#ff9f0a",
};

const drawChef = (ctx, x, y, frame, dead) => {
  const cx = x + CHEF_W / 2;
  ctx.fillStyle = dead ? "#888" : "#fff";
  ctx.beginPath(); ctx.fillRect(cx - 10, y - 14, 20, 6);
  ctx.beginPath(); ctx.ellipse(cx, y - 14, 13, 6, 0, 0, Math.PI * 2); ctx.fill();
  ctx.fillRect(cx - 8, y - 30, 16, 18);
  ctx.beginPath(); ctx.ellipse(cx, y - 30, 10, 4, 0, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = dead ? "#c8a87a" : "#f5c99a";
  ctx.beginPath(); ctx.ellipse(cx, y - 2, 13, 13, 0, 0, Math.PI * 2); ctx.fill();
  if (dead) {
    ctx.strokeStyle = "#333"; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(cx - 7, y - 7); ctx.lineTo(cx - 4, y - 4); ctx.moveTo(cx - 4, y - 7); ctx.lineTo(cx - 7, y - 4); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(cx + 4, y - 7); ctx.lineTo(cx + 7, y - 4); ctx.moveTo(cx + 7, y - 7); ctx.lineTo(cx + 4, y - 4); ctx.stroke();
  } else {
    ctx.fillStyle = "#333";
    ctx.beginPath(); ctx.arc(cx - 5, y - 5, 2, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(cx + 5, y - 5, 2, 0, Math.PI * 2); ctx.fill();
  }
  ctx.fillStyle = dead ? "#888" : "#e8e8e8"; ctx.fillRect(cx - 12, y + 10, 24, 22);
  ctx.fillStyle = dead ? "#aaa" : "#fff"; ctx.fillRect(cx - 6, y + 10, 12, 22);
  const legOff = Math.sin(frame * 0.4) * 5;
  ctx.fillStyle = dead ? "#555" : "#444";
  ctx.fillRect(cx - 10, y + 30, 8, 14 + (dead ? 0 : legOff));
  ctx.fillRect(cx + 2, y + 30, 8, 14 - (dead ? 0 : legOff));
};

export default function ChefRunGame({ onBack }) {
  const canvasRef = useRef(null), stateRef = useRef(null), rafRef = useRef(null), lastTs = useRef(0);
  const [phase, setPhase] = useState("idle");
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(() => parseInt(localStorage.getItem("chefrun_best") || "0"));
  const emojiCache = useRef({});

  const preloadEmoji = (emoji) => {
    if (emojiCache.current[emoji]) return emojiCache.current[emoji];
    const c = document.createElement("canvas"); c.width = 48; c.height = 48;
    const ctx = c.getContext("2d"); ctx.font = "36px serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText(emoji, 24, 24);
    emojiCache.current[emoji] = c; return c;
  };

  useEffect(() => { OBSTACLES.forEach(o => preloadEmoji(o.emoji)); preloadEmoji("☁️"); }, []);

  const buildState = () => ({
    chef: { y: GROUND_Y - CHEF_H, vy: 0, onGround: true, frame: 0 },
    obstacles: [],
    clouds: [{ x: W * 0.3, y: 20, speed: 0.4, scale: 1 }, { x: W * 0.7, y: 45, speed: 0.25, scale: 0.7 }],
    stars: Array.from({ length: 18 }, (_, i) => ({ x: (i / 18) * W, y: Math.random() * (GROUND_Y - 60) * 0.6, twinkle: Math.random() * Math.PI * 2 })),
    speed: INITIAL_SPEED, frame: 0, score: 0, nextObstacle: 90, phase: "playing", groundOffset: 0,
  });

  const startGame = useCallback(() => { stateRef.current = buildState(); setPhase("playing"); setScore(0); }, []);

  const jump = useCallback(() => {
    const s = stateRef.current;
    if (!s || s.phase === "dead") { startGame(); return; }
    if (s.phase === "playing" && s.chef.onGround) { s.chef.vy = JUMP_FORCE; s.chef.onGround = false; }
  }, [startGame]);

  useEffect(() => {
    const onKey = (e) => { if (e.code === "Space" || e.key === "ArrowUp") { e.preventDefault(); jump(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [jump]);

  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const loop = (ts) => {
      rafRef.current = requestAnimationFrame(loop);
      const dt = Math.min(ts - lastTs.current, 50); lastTs.current = ts;
      const s = stateRef.current;
      // ── draw ──
      const sky = ctx.createLinearGradient(0, 0, 0, H);
      sky.addColorStop(0, "#0a0e2a"); sky.addColorStop(1, "#1a2060");
      ctx.fillStyle = sky; ctx.fillRect(0, 0, W, H);
      if (!s) {
        for (let i = 0; i < 20; i++) { const x = (i * 127.3) % W, y = (i * 83.7) % (GROUND_Y - 40); ctx.globalAlpha = 0.3 + Math.sin(ts * 0.002 + i) * 0.3; ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(x, y, 1, 0, Math.PI * 2); ctx.fill(); }
        ctx.globalAlpha = 1;
        ctx.fillStyle = "#2a3a6a"; ctx.fillRect(0, GROUND_Y, W, H - GROUND_Y);
        ctx.strokeStyle = "#3a5090"; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(0, GROUND_Y); ctx.lineTo(W, GROUND_Y); ctx.stroke();
        drawChef(ctx, CHEF_X, GROUND_Y - CHEF_H, Math.floor(ts / 80), false);
        ctx.fillStyle = "rgba(245,245,247,0.92)"; ctx.font = "bold 15px -apple-system,sans-serif"; ctx.textAlign = "center"; ctx.fillText("Chef Run 🍴", W / 2, H / 2 - 14);
        ctx.fillStyle = "rgba(245,245,247,0.4)"; ctx.font = "12px -apple-system,sans-serif"; ctx.fillText("Tocca o premi Spazio per iniziare", W / 2, H / 2 + 8);
        return;
      }
      s.stars.forEach(st => { ctx.globalAlpha = 0.4 + Math.sin(st.twinkle) * 0.4; ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(st.x, st.y, 1, 0, Math.PI * 2); ctx.fill(); });
      ctx.globalAlpha = 1;
      s.clouds.forEach(c => { const img = emojiCache.current["☁️"]; if (img) { ctx.globalAlpha = 0.5 * c.scale; ctx.drawImage(img, c.x - 24 * c.scale, c.y - 12 * c.scale, 48 * c.scale, 48 * c.scale); ctx.globalAlpha = 1; } });
      ctx.fillStyle = "#2a3a6a"; ctx.fillRect(0, GROUND_Y, W, H - GROUND_Y);
      ctx.strokeStyle = "#3a5090"; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(0, GROUND_Y); ctx.lineTo(W, GROUND_Y); ctx.stroke();
      ctx.strokeStyle = "rgba(90,120,200,0.3)"; ctx.lineWidth = 1;
      for (let x = -s.groundOffset; x < W; x += 40) { ctx.beginPath(); ctx.moveTo(x, GROUND_Y); ctx.lineTo(x, H); ctx.stroke(); }
      s.obstacles.forEach(o => { const img = emojiCache.current[o.emoji]; if (img) ctx.drawImage(img, o.x, o.y, o.w, o.h); });
      drawChef(ctx, CHEF_X, s.chef.y + CHEF_H - 8, s.frame, s.phase === "dead");
      if (s.phase === "dead") {
        ctx.fillStyle = "rgba(5,6,15,0.72)"; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = "rgba(245,245,247,0.92)"; ctx.font = "bold 15px -apple-system,sans-serif"; ctx.textAlign = "center"; ctx.fillText("Game Over", W / 2, H / 2 - 12);
        ctx.fillStyle = "rgba(245,245,247,0.45)"; ctx.font = "12px -apple-system,sans-serif"; ctx.fillText("Tocca per ricominciare", W / 2, H / 2 + 10);
      }
      if (s.phase !== "playing") return;
      // ── physics ──
      s.frame++; s.chef.frame = s.frame;
      s.speed += SPEED_INCREMENT * dt;
      s.groundOffset = (s.groundOffset + s.speed) % 40;
      s.chef.vy += GRAVITY; s.chef.y += s.chef.vy;
      if (s.chef.y >= GROUND_Y - CHEF_H) { s.chef.y = GROUND_Y - CHEF_H; s.chef.vy = 0; s.chef.onGround = true; } else { s.chef.onGround = false; }
      s.clouds.forEach(c => { c.x -= c.speed; if (c.x < -80) c.x = W + 80; });
      s.stars.forEach(st => st.twinkle += 0.05);
      s.nextObstacle--;
      if (s.nextObstacle <= 0) {
        const o = OBSTACLES[Math.floor(Math.random() * OBSTACLES.length)];
        const count = Math.random() < 0.25 ? 2 : 1;
        for (let i = 0; i < count; i++) s.obstacles.push({ x: W + i * (o.w + 8), y: GROUND_Y - o.h, ...o });
        s.nextObstacle = 60 + Math.random() * 80;
      }
      s.obstacles = s.obstacles.map(o => ({ ...o, x: o.x - s.speed })).filter(o => o.x > -60);
      s.score += 0.1;
      if (Math.floor(s.score) !== Math.floor(s.score - 0.1)) setScore(Math.floor(s.score));
      const margin = 6, cx1 = CHEF_X + margin, cx2 = CHEF_X + CHEF_W - margin, cy1 = s.chef.y + margin, cy2 = s.chef.y + CHEF_H - margin;
      for (const o of s.obstacles) {
        if (cx2 > o.x + margin && cx1 < o.x + o.w - margin && cy2 > o.y + margin && cy1 < o.y + o.h - margin) {
          s.phase = "dead"; setPhase("dead");
          const fs = Math.floor(s.score);
          if (fs > best) { setBest(fs); localStorage.setItem("chefrun_best", String(fs)); }
          return;
        }
      }
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [best]);

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "1.5rem", gap: "1rem", userSelect: "none", fontFamily: "-apple-system,'SF Pro Display','Helvetica Neue',sans-serif" }}>

      {/* HUD */}
      <div style={{ display: "flex", gap: "0", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, overflow: "hidden", width: "100%", maxWidth: W }}>
        {[{ label: "Punteggio", value: score, color: C.text }, { label: "Record", value: best, color: C.orange }].map((s, i) => (
          <div key={i} style={{ flex: 1, padding: "0.85rem 1.25rem", borderRight: i === 0 ? `1px solid ${C.border}` : "none" }}>
            <p style={{ margin: "0 0 0.2rem", fontSize: "0.65rem", color: C.secondary, fontWeight: 500 }}>{s.label}</p>
            <p style={{ margin: 0, fontSize: "1.5rem", fontWeight: 700, color: s.color, letterSpacing: "-0.5px", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Canvas */}
      <div style={{ width: "100%", maxWidth: W, borderRadius: 18, overflow: "hidden", border: `1px solid ${C.border}`, boxShadow: "0 12px 40px rgba(0,0,0,0.55)", cursor: "pointer", touchAction: "none" }}
        onClick={jump} onTouchStart={e => { e.preventDefault(); jump(); }}>
        <canvas ref={canvasRef} width={W} height={H} style={{ display: "block", width: "100%" }} />
      </div>

      <p style={{ margin: 0, fontSize: "0.72rem", color: C.tertiary }}>Spazio · ↑ · Tap — per saltare</p>

      {onBack && (
        <button onClick={onBack} style={{ padding: "0.62rem 1.25rem", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, color: C.secondary, fontSize: "0.82rem", fontWeight: 500, cursor: "pointer", fontFamily: "inherit", transition: "background 160ms ease" }}
          onMouseEnter={e => e.currentTarget.style.background = C.surfaceHover}
          onMouseLeave={e => e.currentTarget.style.background = C.surface}
        >← Torna ai giochi</button>
      )}
    </div>
  );
}
